// Developer Portal REST client for projects, API keys, and access tokens
// Expects VITE_API_BASE_URL and an auth token in localStorage under 'auth_token'

const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL;

async function parseBody(res) {
  const text = await res.text();
  try { return text ? JSON.parse(text) : null; } catch (_) { return null; }
}

async function apiFetch(path, options = {}) {
  if (!API_BASE_URL) {
    const err = new Error('Missing VITE_API_BASE_URL. Please set it in your environment.');
    err.status = 500;
    throw err;
  }
  const token = localStorage?.getItem('auth_token');
  const headers = {
    'Accept': 'application/json',
    ...(options?.body ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
  const res = await fetch(`${API_BASE_URL}${path}`, { ...options, headers });
  const body = await parseBody(res);
  if (!res.ok) {
    const err = new Error(body?.error || body?.message || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return body;
}

function normalizeList(data) {
  if (Array.isArray(data?.invoices)) return data.invoices;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data)) return data;
  if (data?.data && Array.isArray(data.data)) return data.data;
  return [];
}

export const devportalApi = {
  // Projects
  async listProjects() {
    const data = await apiFetch('/projects', { method: 'GET' });
    return normalizeList(data);
  },

  // Quota
  async getTenantQuota() {
    return await apiFetch('/quota/tenant', { method: 'GET' });
  },
  async updateTenantQuota({ message_limit, member_limit }) {
    return await apiFetch('/quota/tenant', {
      method: 'PUT',
      body: JSON.stringify({ message_limit, member_limit })
    });
  },
  async listProjectQuotas() {
    return normalizeList(await apiFetch('/projects/quotas', { method: 'GET' }));
  },
  async getProjectQuota(projectId) {
    const res = await apiFetch(`/quota/project?projectId=${encodeURIComponent(projectId)}`, { method: 'GET' });
    const pq = res?.data?.projectQuota ?? res?.data ?? res;

    const hasNotSpecific = pq?.hasNotSpeceficQuota ?? pq?.hasNotSpecificQuota;
    if (hasNotSpecific === true) {
      return { message_limit: null, member_limit: null };
    }

    return {
      message_limit: pq?.messageLimit ?? pq?.message_limit ?? null,
      member_limit: pq?.memberLimit ?? pq?.member_limit ?? null,
      message_used: pq?.messageCount ?? pq?.message_used ?? null,
      message_remains: pq?.messageRemains ?? pq?.message_remains ?? null
    };
  },
  async updateProjectQuota(projectId, { message_limit, member_limit, useInherentQuota }) {
    const body = {
      messageLimit: message_limit,
      memberLimit: member_limit
    };

    if (useInherentQuota !== undefined) {
      body.useInherentQuota = useInherentQuota;
    }

    return await apiFetch(`/quota/project?projectId=${encodeURIComponent(projectId)}`, {
      method: 'PUT',
      body: JSON.stringify(body)
    });
  },

  // API Keys
  async listApiKeys() {
    const data = await apiFetch('/api-keys', { method: 'GET' });
    return normalizeList(data);
  },
  async createApiKey({ key_name, token_hash, last_four, key_prefix, project_id }) {
    return await apiFetch('/api-keys', {
      method: 'POST',
      body: JSON.stringify({ key_name, token_hash, last_four, key_prefix, project_id: project_id || null })
    });
  },
  async revokeApiKey(id) {
    // Support either PATCH with revoked_at or dedicated revoke endpoint
    try {
      return await apiFetch(`/api-keys/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ revoked_at: new Date().toISOString() })
      });
    } catch (e) {
      if (e?.status === 405 || e?.status === 404) {
        return await apiFetch(`/api-keys/${id}/revoke`, { method: 'POST' });
      }
      throw e;
    }
  },

  // Access Tokens
  async listAccessTokens() {
    const data = await apiFetch('/access-tokens', { method: 'GET' });
    return normalizeList(data);
  },
  async createAccessToken({ name, token_hash, last_four, token_prefix, project_id }) {
    return await apiFetch('/access-tokens', {
      method: 'POST',
      body: JSON.stringify({ name, token_hash, last_four, token_prefix, project_id: project_id || null })
    });
  },
  async revokeAccessToken(id) {
    try {
      return await apiFetch(`/access-tokens/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ revoked_at: new Date().toISOString() })
      });
    } catch (e) {
      if (e?.status === 405 || e?.status === 404) {
        return await apiFetch(`/access-tokens/${id}/revoke`, { method: 'POST' });
      }
      throw e;
    }
  },
  async getUsageAnalytics({ range = '7d', project_id } = {}) {
    const url = project_id
      ? `/analytics/usage?range=${encodeURIComponent(range)}&project_id=${encodeURIComponent(project_id)}`
      : `/analytics/usage?range=${encodeURIComponent(range)}`;
    const data = await apiFetch(url, { method: 'GET' });
    return data;
  },
  async getAnalyticsSummary() {
    const data = await apiFetch('/analytics/summary', { method: 'GET' });
    return data;
  },

  // Dashboard Analytics
  async getDashboardAnalytics() {
    return await apiFetch('/analytics/dashboard', { method: 'GET' });
  },

  async getDashboardSummary() {
    return await apiFetch('/analytics/dashboard/summary', { method: 'GET' });
  },

  // Plans
  async getPlans() {
    const data = await apiFetch('/plans', { method: 'GET' });
    return normalizeList(data);
  },

  async getUserPlans() {
    const data = await apiFetch('/user-plans', { method: 'GET' });
    return normalizeList(data);
  },

  // Payment
  async createPaymentSession({ plan_id, success_url, cancel_url }) {
    return await apiFetch('/CreatePaymentSession', {
      method: 'POST',
      body: JSON.stringify({ plan_id, success_url, cancel_url })
    });
  },

  async getPaymentsHistory() {
    const data = await apiFetch('/payments-history', { method: 'GET' });
    return normalizeList(data);
  },

  // User
  async getUserInfo() {
    return await apiFetch('/users/me', { method: 'GET' });
  },

  // Invoices
  async listInvoices() {
    const data = await apiFetch('/invoices', { method: 'GET' });
    return normalizeList(data);
  },
  async createInvoice({ plan_id, description }) {
    return await apiFetch('/invoices', {
      method: 'POST',
      body: JSON.stringify({ plan_id, description })
    });
  },
  async getInvoiceDetails(id) {
    return await apiFetch(`/invoices/${id}`, { method: 'GET' });
  },
  async getInvoiceStatus(id) {
    return await apiFetch(`/invoices/${id}/status`, { method: 'GET' });
  },
  async payInvoice(id, { success_url, cancel_url } = {}) {
    // Ensure id is a string and path is correct
    const invoiceId = String(id);
    return await apiFetch(`/invoices/${invoiceId}/pay`, {
      method: 'POST',
      body: JSON.stringify({ success_url, cancel_url })
    });
  }
};

export default devportalApi;
